export const PROJECT_LIST_PATH = "/projects";
export const PROJECT_CREATE_PATH = `${PROJECT_LIST_PATH}/new`;
/** Route param for `/projects/new` — same ProjectDetail page as existing projects. */
export const PROJECT_NEW_UUID = "new";

export const projectDetailPath = (uuid: string): string => `${PROJECT_LIST_PATH}/${uuid}`;

export const PROJECT_LIST_TAB_OVERVIEW = "overview";
export const PROJECT_LIST_TAB_SUPPORT = "support";

export type ProjectListTab = typeof PROJECT_LIST_TAB_OVERVIEW | typeof PROJECT_LIST_TAB_SUPPORT;

export const PROJECT_SUPPORT_TYPE_HIRE = "HIRE";
export const PROJECT_SUPPORT_TYPE_HELP = "HELP";
export const PROJECT_SUPPORT_TYPE_STAKEHOLDER_CHANGES = "STAKEHOLDER_CHANGES";
export const PROJECT_SUPPORT_TYPE_ALL = "ALL";

export type ProjectSupportType =
    | typeof PROJECT_SUPPORT_TYPE_HIRE
    | typeof PROJECT_SUPPORT_TYPE_HELP
    | typeof PROJECT_SUPPORT_TYPE_STAKEHOLDER_CHANGES;

export type ProjectSupportTypeFilter = ProjectSupportType | typeof PROJECT_SUPPORT_TYPE_ALL;

export const PROJECT_SUPPORT_STATUS_OPEN = "OPEN";
export const PROJECT_SUPPORT_STATUS_IN_PROGRESS = "IN_PROGRESS";
export const PROJECT_SUPPORT_STATUS_DONE = "DONE";
export const PROJECT_SUPPORT_STATUS_WITHDRAWN = "WITHDRAWN";
export const PROJECT_SUPPORT_STATUS_ALL = "ALL";

export type ProjectSupportStatus =
    | typeof PROJECT_SUPPORT_STATUS_OPEN
    | typeof PROJECT_SUPPORT_STATUS_IN_PROGRESS
    | typeof PROJECT_SUPPORT_STATUS_DONE
    | typeof PROJECT_SUPPORT_STATUS_WITHDRAWN;

export type ProjectSupportStatusFilter = ProjectSupportStatus | typeof PROJECT_SUPPORT_STATUS_ALL;

export const PROJECT_SUPPORT_STATUS_OPTIONS: ProjectSupportStatus[] = [
    PROJECT_SUPPORT_STATUS_OPEN,
    PROJECT_SUPPORT_STATUS_IN_PROGRESS,
    PROJECT_SUPPORT_STATUS_DONE,
    PROJECT_SUPPORT_STATUS_WITHDRAWN,
];

/** Statuses staff can set from the support inbox (withdraw uses a dedicated endpoint). */
export const PROJECT_SUPPORT_STATUS_EDIT_OPTIONS: ProjectSupportStatus[] = [
    PROJECT_SUPPORT_STATUS_OPEN,
    PROJECT_SUPPORT_STATUS_IN_PROGRESS,
    PROJECT_SUPPORT_STATUS_DONE,
];

/**
 * Stable codes for implemented phase screens — must match `DASHLY_PROJECT_PHASE.CODE`.
 * Order and labels come from the phase catalog API.
 */
/** Stap 1 — Projectnaam (klant, naam, omschrijving). Sits before process & gap analysis. */
export const PROJECT_PHASE_CODE_PROJECT_NAME = "project_name";
/** Stap 2 — Proces & gap-analyse. Sits before `functional_design` in the phase catalog (SORT_ORDER). */
export const PROJECT_PHASE_CODE_PROCESS_GAP_ANALYSIS = "process_gap_analysis";
export const PROJECT_PHASE_CODE_FUNCTIONAL_DESIGN = "functional_design";
export const PROJECT_PHASE_CODE_STAKEHOLDER_AGREEMENT = "stakeholder_agreement";
export const PROJECT_PHASE_CODE_DASHBOARD_TEMPLATE = "dashboard_template";
export const PROJECT_PHASE_CODE_BUSINESS_LOGIC = "business_logic";
export const PROJECT_PHASE_CODE_DATASET_VALIDATION_A = "dataset_validation_a";
export const PROJECT_PHASE_CODE_MODELLING_DECISION = "modelling_decision";
export const PROJECT_PHASE_CODE_DATASET_VALIDATION_B = "dataset_validation_b";
export const PROJECT_PHASE_CODE_SCOPE_APPROVAL = "scope_approval";
export const PROJECT_PHASE_CODE_DATA_MAPPING = "data_mapping";
export const PROJECT_PHASE_CODE_DASHBOARD_SETUP = "dashboard_setup";
export const PROJECT_PHASE_CODE_DASHBOARD_VALIDATION = "dashboard_validation";
export const PROJECT_PHASE_CODE_DASHBOARD_LIVE = "dashboard_live";

export const PROJECT_SETUP_STATUS_NOT_STARTED = "NOT_STARTED";
export const PROJECT_SETUP_STATUS_IN_PROGRESS = "IN_PROGRESS";
export const PROJECT_SETUP_STATUS_DONE = "DONE";

/** Project GET nested payload scope — phase = active fase only (fast first paint). */
export const PROJECT_NESTED_SCOPE_PHASE = "phase";
export const PROJECT_NESTED_SCOPE_FULL = "full";

export type ProjectSetupStatus =
    | typeof PROJECT_SETUP_STATUS_NOT_STARTED
    | typeof PROJECT_SETUP_STATUS_IN_PROGRESS
    | typeof PROJECT_SETUP_STATUS_DONE;

export const PROJECT_SETUP_STATUS_OPTIONS: ProjectSetupStatus[] = [
    PROJECT_SETUP_STATUS_NOT_STARTED,
    PROJECT_SETUP_STATUS_IN_PROGRESS,
    PROJECT_SETUP_STATUS_DONE,
];

export const PROJECT_MODELLING_VIEW_STRATEGY_SINGLE = "single_view";
export const PROJECT_MODELLING_VIEW_STRATEGY_MULTIPLE = "multiple_views";

export type ProjectModellingViewStrategy =
    | typeof PROJECT_MODELLING_VIEW_STRATEGY_SINGLE
    | typeof PROJECT_MODELLING_VIEW_STRATEGY_MULTIPLE;

/** Phases auto-skipped in advance/nav while UI is not built — keep empty when shell phases stay navigable. */
export const PROJECT_TEMPORARILY_SKIPPED_PHASE_CODES = [] as const;

/**
 * Visible shell phases with no completion gate: once reached, the next phase is also selectable.
 * Empty while every catalog phase has a real screen / completion gate (incl. dashboard_template).
 */
export const PROJECT_SHELL_PASS_THROUGH_PHASE_CODES = [] as const;

export const PROJECT_BLOCK_STATUS_CONCEPT = "CONCEPT";
export const PROJECT_BLOCK_STATUS_WAITING_DASHLY = "WAITING_DASHLY";
export const PROJECT_BLOCK_STATUS_WAITING_CUSTOMER = "WAITING_CUSTOMER";
export const PROJECT_BLOCK_STATUS_APPROVED = "APPROVED";

export const PROJECT_TECHNICAL_MAPPING_MODE_BASIC = "basic";
export const PROJECT_TECHNICAL_MAPPING_MODE_ADVANCED = "advanced";

/** Persisted when basis-modus only a dataset is chosen (TABLE_ID is NOT NULL in DB). */
export const PROJECT_BASIC_DATASET_ONLY_TABLE_PLACEHOLDER = "_";

/**
 * High-level list-view stages (presentation only) — derived from the active phase.
 * Codes are stable filter/badge keys; labels come from `project.list.stage.*` translations.
 */
export const PROJECT_LIST_STAGE_INTAKE = "intake";
export const PROJECT_LIST_STAGE_BLUEPRINT = "blueprint";
export const PROJECT_LIST_STAGE_ALIGNMENT = "alignment";
export const PROJECT_LIST_STAGE_RESOLUTION = "resolution";
export const PROJECT_LIST_STAGE_LIVE = "live";

export const PROJECT_LIST_STAGE_CODES = [
    PROJECT_LIST_STAGE_INTAKE,
    PROJECT_LIST_STAGE_BLUEPRINT,
    PROJECT_LIST_STAGE_ALIGNMENT,
    PROJECT_LIST_STAGE_RESOLUTION,
    PROJECT_LIST_STAGE_LIVE,
] as const;

export type ProjectListStageCode = (typeof PROJECT_LIST_STAGE_CODES)[number];
